import { z, type ZodType } from 'zod';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
export type AuthScope = 'public' | 'authenticated';

declare const requestTypeSymbol: unique symbol;
declare const responseTypeSymbol: unique symbol;
declare const pathParamsTypeSymbol: unique symbol;
declare const queryTypeSymbol: unique symbol;

export interface ApiEndpointMeta<
  Method extends HttpMethod = HttpMethod,
  Path extends string = string,
  Scope extends AuthScope = AuthScope,
> {
  method: Method;
  path: Path;
  auth: Scope;
}

export interface ApiEndpointSchemas<
  Request = void,
  Response = void,
  PathParams = void,
  Query = void,
> {
  request?: ZodType<Request>;
  response?: ZodType<Response>;
  pathParams?: ZodType<PathParams>;
  query?: ZodType<Query>;
}

export type ApiEndpointContract<
  Method extends HttpMethod = HttpMethod,
  Path extends string = string,
  Scope extends AuthScope = AuthScope,
  Request = void,
  Response = void,
  PathParams = void,
  Query = void,
> = ApiEndpointMeta<Method, Path, Scope> & {
  schemas: ApiEndpointSchemas<Request, Response, PathParams, Query>;
  readonly [requestTypeSymbol]: Request;
  readonly [responseTypeSymbol]: Response;
  readonly [pathParamsTypeSymbol]: PathParams;
  readonly [queryTypeSymbol]: Query;
};

export function defineEndpoint<
  Request = void,
  Response = void,
  PathParams = void,
  Query = void,
  const Method extends HttpMethod = HttpMethod,
  const Path extends string = string,
  const Scope extends AuthScope = AuthScope,
>(
  meta: ApiEndpointMeta<Method, Path, Scope> & {
    schemas?: ApiEndpointSchemas<Request, Response, PathParams, Query>;
  },
): ApiEndpointContract<Method, Path, Scope, Request, Response, PathParams, Query> {
  return {
    ...meta,
    schemas: meta.schemas ?? {},
  } as ApiEndpointContract<Method, Path, Scope, Request, Response, PathParams, Query>;
}

export const idPathParamsSchema = z.object({
  id: z.number().int().positive(),
});

export type IdPathParams = z.infer<typeof idPathParamsSchema>;

type PathParamValue = string | number | boolean;

type ExtractPathParamKeys<Path extends string> =
  Path extends `${string}:${infer Param}/${infer Rest}`
    ? Param | ExtractPathParamKeys<`/${Rest}`>
    : Path extends `${string}:${infer Param}`
      ? Param
      : never;

export type PathTemplateParams<Path extends string> =
  [ExtractPathParamKeys<Path>] extends [never]
    ? void
    : Record<ExtractPathParamKeys<Path>, PathParamValue>;

export function resolvePathTemplate<Path extends string>(template: Path, params?: object): string {
  const pathParams = (params ?? {}) as Record<string, PathParamValue>;

  return template.replace(/:([A-Za-z0-9_]+)/g, (segment, key: string) => {
    if (!(key in pathParams)) {
      return segment;
    }

    return encodeURIComponent(String(pathParams[key]));
  });
}
